import {
  Box, Collapse, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Tooltip, Typography
} from '@mui/material';
import { AssignmentInd } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { AiOutlineHome } from 'react-icons/ai';
import { BiWrench } from 'react-icons/bi';
import { FaTasks, FaUsers, FaClone, FaUserCheck } from 'react-icons/fa';
import { FiArchive, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { GiOnTarget, GiScrollQuill } from 'react-icons/gi';
import { ImCompass2, ImStatsDots, ImDownload3 } from 'react-icons/im';
import { GiTeamIdea, GiRadarSweep } from 'react-icons/gi';
import { RiMailSettingsLine } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { v4 } from 'uuid';
import LoadingSpinner from '../../components/LoadingSpinner';
import { ROLE } from '../../enums/rolesEnum';
import CandidatImport from '../../pages/Import/Candidat/CandidatImport';
import ContactImport from '../../pages/Import/Contact/ContactImport';
import { getUserData } from '../../utils/FunctionHelpers';
import { setToggleSidebar } from '../../redux/toggle/actions/toggleActions';
import { getAll } from '../../services/CollaborateurService';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { BiFile } from 'react-icons/bi';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED = 68;

const NavWrapper = styled(Box)(({ iscollapsed, indrawer, theme }) => (indrawer === 'true'
  // Inside the mobile drawer: fills the drawer instead of being a fixed sidebar.
  ? {
    position: 'relative',
    height: '100%',
    width: '100%',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }
  : {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: iscollapsed === 'true' ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH,
    background: '#ffffff',
    borderRight: '1px solid #e2e8f0',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: 'hidden',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 0 12px rgba(0,0,0,0.06)',
    [theme.breakpoints.down('sm')]: { display: 'none' },
  }));

const LogoArea = styled(Box)(({ iscollapsed }) => ({
  height: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: iscollapsed === 'true' ? 'center' : 'flex-start',
  padding: iscollapsed === 'true' ? '0 12px' : '0 20px',
  borderBottom: '1px solid #e2e8f0',
  flexShrink: 0,
}));

const NavScrollArea = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '12px 10px',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: 2 },
});

const NavItem = styled(NavLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 10,
  marginBottom: 2,
  textDecoration: 'none',
  color: '#64748b',
  transition: 'all 0.18s ease',
  cursor: 'pointer',
  '&:hover': {
    background: '#f1f5f9',
    color: '#03407b',
  },
  '&.active': {
    background: 'linear-gradient(135deg, #0556a4, #03407b)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(3,64,123,0.22)',
  },
}));

const SubNavItem = styled(NavLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px 8px 20px',
  borderRadius: 8,
  marginBottom: 2,
  textDecoration: 'none',
  color: '#94a3b8',
  transition: 'all 0.18s ease',
  fontSize: '1rem',
  '&:hover': {
    background: '#f8fafc',
    color: '#03407b',
  },
  '&.active': {
    background: '#ebf4fb',
    color: '#03407b',
  },
}));

const SectionLabel = styled(Typography)({
  fontSize: '0.8125rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#94a3b8',
  textTransform: 'uppercase',
  padding: '12px 12px 4px',
  userSelect: 'none',
});

const CollapseToggle = styled(Box)({
  padding: '10px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'flex-end',
  flexShrink: 0,
});

const NavLabel = styled(Typography)(({ iscollapsed }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  opacity: iscollapsed === 'true' ? 0 : 1,
  transition: 'opacity 0.15s ease',
  flex: 1,
}));

const ToolsButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 10,
  marginBottom: 2,
  color: '#64748b',
  transition: 'all 0.18s ease',
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    background: '#f1f5f9',
    color: '#03407b',
  },
}));

const VerticalNavWrapper = ({ sideBarCollapsed = false, handleClose = () => {}, reloadNotifications, inDrawer = false }) => {
  const [collapsed, setCollapsed] = useState(sideBarCollapsed);
  const dispatch = useDispatch();
  const [authData, setAuthData] = useState({ isLoading: true, userData: null });
  const notAllowedRoles = [ROLE.ADMIN, ROLE.COMMERCIAL, ROLE.TESTEUR];
  const [outilsOpen, setOutilsOpen] = useState(false);
  const [modalState, setModalState] = useState({ open: false });
  const [modalStateContact, setModalStateContact] = useState({ open: false });
  const [hasContractEndingSoon, setHasContractEndingSoon] = useState(false);
  const { collaborate, documentContrat, conge } = useFeatureFlags();

  const handleOpen = () => setModalState({ open: true });
  const handleOpenContact = () => setModalStateContact({ open: true });

  useEffect(() => {
    const userData = getUserData();
    if (userData && userData.role) {
      setAuthData({ isLoading: false, userData });
    } else {
      window.location.replace('/login');
    }
  }, []);

  const fetchAndCheckContracts = async () => {
    try {
      const response = await getAll({ page: 1, limit: 5, search: '', years: [], contrats: [] });
      const collaborateurs = response.data.data || [];
      const endingSoon = collaborateurs.some((employee) => {
        if (!employee?.dateFinContrat) return false;
        const now = new Date();
        const end = new Date(employee.dateFinContrat);
        const diffInDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        return diffInDays >= 0 && diffInDays <= 15;
      });
      setHasContractEndingSoon(!!endingSoon);
    } catch {}
  };

  useEffect(() => {
    const canSeeCollaborateurBadge =
      collaborate && authData.userData?.role?.some((role) => role === ROLE.DIRECTION);

    if (canSeeCollaborateurBadge) fetchAndCheckContracts();
  }, [authData.userData, collaborate]);

  if (authData.isLoading) return <LoadingSpinner />;

  const isCollapsed = collapsed.toString();

  const hasRole = (roles) => authData.userData.role.some((r) => roles.includes(r));

  const navItems = [
    {
      to: '/accueil',
      icon: <AiOutlineHome size={18} />,
      label: 'Accueil',
      notAllowedRoles: [ROLE.ADMIN, ROLE.COMMERCIAL],
    },
    {
      to: '/tachescandidat',
      icon: <FaTasks size={16} />,
      label: 'Mes tâches',
      notAllowedRoles,
    },
    {
      to: '/selections',
      icon: <FiArchive size={16} />,
      label: 'Mes sélections',
      notAllowedRoles: [ROLE.TESTEUR],
    },
    {
      to: '/missions',
      icon: <GiOnTarget size={18} />,
      label: 'Missions',
      notAllowedRoles,
    },
    {
      to: '/candidats-offre',
      icon: <AssignmentInd sx={{ fontSize: 18 }} />,
      label: "Positionnement",
      notAllowedRoles: [],
    },
    {
      to: '/collaborateurs',
      icon: <GiTeamIdea size={18} />,
      label: 'Collaborateurs',
      allowedRoles: [ROLE.DIRECTION],
      visible: collaborate,
      badge: hasContractEndingSoon,
    },
    {
      to: '/demandes',
      icon: <GiScrollQuill size={18} />,
      label: 'Demandes',
      allowedRoles: [ROLE.DIRECTION],
      visible: collaborate,
    },
    {
      to: '/matching-candidates',
      icon: <FaUserCheck size={16} />,
      label: 'Matching candidats',
      notAllowedRoles: [ROLE.COMMERCIAL, ROLE.TESTEUR],
    },
  ];

  const toolsItems = [
    {
      to: '/reporting/intern',
      icon: <ImStatsDots size={15} />,
      label: 'Reporting interne',
      roles: [ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER, ROLE.TALENT_MANAGER],
    },
    {
      to: '/talent-board',
      icon: <GiRadarSweep size={15} />,
      label: 'Talent board',
      roles: [ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER, ROLE.TALENT_MANAGER],
    },
    {
      to: '/gomail',
      icon: <MailOutlineIcon sx={{ fontSize: 16 }} />,
      label: 'GoMail',
      roles: [ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER, ROLE.TALENT_MANAGER],
    },
    {
      to: '/sourcing',
      icon: <TravelExploreIcon sx={{ fontSize: 16 }} />,
      label: 'Sourcing IA',
      roles: [ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER, ROLE.TALENT_MANAGER],
    },
    {
      to: '/doublons',
      icon: <FaClone size={15} />,
      label: 'Doublons candidats',
      roles: [ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER, ROLE.TALENT_MANAGER],
    },
    {
      to: '/pilotage',
      icon: <ImCompass2 size={15} />,
      label: 'Pilotage',
      roles: [ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER, ROLE.TALENT_MANAGER],
    },
    {
      to: '/emailtype',
      icon: <RiMailSettingsLine size={15} />,
      label: 'Messages type',
      roles: [ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER],
    },
    {
      to: '/users',
      icon: <FaUsers size={15} />,
      label: 'Utilisateurs',
      roles: [ROLE.ADMIN, ROLE.DIRECTION, ROLE.MANAGER],
    },
  ];

  const showTools = hasRole([ROLE.ADMIN, ROLE.ACQUISITION_MANAGER, ROLE.DIRECTION, ROLE.MANAGER, ROLE.TALENT_MANAGER]);

  return (
    <NavWrapper iscollapsed={isCollapsed} indrawer={inDrawer ? 'true' : 'false'}>
      <LogoArea iscollapsed={isCollapsed}>
        {collapsed ? (
          <Box sx={{
            width: 32, height: 32, borderRadius: '8px',
            background: 'linear-gradient(135deg, #0556a4, #03407b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(3,64,123,0.25)',
          }}>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>G</Typography>
          </Box>
        ) : (
          <img
            src={process.env.REACT_APP_LOGO_URL}
            alt="Logo"
            style={{ height: 30 }}
          />
        )}
      </LogoArea>

      <NavScrollArea>
        {!collapsed && <SectionLabel>Navigation</SectionLabel>}

        {navItems.map((item) => {
          const { notAllowedRoles: nar = [], allowedRoles: ar = [] } = item;
          const hasAccess =
            (ar.length > 0
              ? authData.userData.role.some((r) => ar.includes(r))
              : !authData.userData.role.some((r) => nar.includes(r)))
            && (item.visible === undefined || item.visible);

          if (!hasAccess) return null;

          const content = (
            <NavItem key={v4()} to={item.to} onClick={handleClose} activeClassName="active">
              <Box sx={{ flexShrink: 0, display: 'flex', width: 20, justifyContent: 'center' }}>
                {item.icon}
              </Box>
              <NavLabel iscollapsed={isCollapsed}>{item.label}</NavLabel>
              {item.badge && (
                <WarningAmberIcon sx={{ fontSize: 14, color: '#f59e0b', flexShrink: 0 }} />
              )}
            </NavItem>
          );

          return collapsed ? (
            <Tooltip key={v4()} title={item.label} placement="right">
              {content}
            </Tooltip>
          ) : content;
        })}

        {showTools && (
          <>
            {!collapsed && <SectionLabel sx={{ mt: 1 }}>Outils</SectionLabel>}

            <Tooltip title={collapsed ? 'Outils' : ''} placement="right">
              <ToolsButton onClick={() => setOutilsOpen(!outilsOpen)}>
                <Box sx={{ flexShrink: 0, display: 'flex', width: 20, justifyContent: 'center' }}>
                  <BiWrench size={18} />
                </Box>
                <NavLabel iscollapsed={isCollapsed}>Outils</NavLabel>
                {!collapsed && (outilsOpen ? <ExpandLess sx={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#94a3b8' }} />)}
              </ToolsButton>
            </Tooltip>

            <Collapse in={outilsOpen && !collapsed} timeout="auto" unmountOnExit>
              {toolsItems.map((item) =>
                hasRole(item.roles) ? (
                  <SubNavItem key={v4()} to={item.to} activeClassName="active" onClick={handleClose}>
                    <Box sx={{ display: 'flex', width: 16, justifyContent: 'center' }}>{item.icon}</Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>{item.label}</Typography>
                  </SubNavItem>
                ) : null
              )}

              {hasRole([ROLE.ADMIN, ROLE.DIRECTION, ROLE.MANAGER]) && (
                <>
                  <SubNavItem as="div" to="#" onClick={handleOpen} sx={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', width: 16, justifyContent: 'center' }}><ImDownload3 size={14} /></Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>Importer candidats</Typography>
                  </SubNavItem>
                  <SubNavItem as="div" to="#" onClick={handleOpenContact} sx={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', width: 16, justifyContent: 'center' }}><ImDownload3 size={14} /></Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>Importer contacts</Typography>
                  </SubNavItem>
                </>
              )}

              {authData.userData.role.includes(ROLE.DIRECTION) && documentContrat && (
                <SubNavItem to="/documents-contrat" activeClassName="active" onClick={handleClose}>
                  <Box sx={{ display: 'flex', width: 16, justifyContent: 'center' }}><BiFile size={14} /></Box>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>Gestion documents</Typography>
                </SubNavItem>
              )}

              {authData.userData.role.includes(ROLE.DIRECTION) && conge && (
                <SubNavItem to="/gestion-conge" activeClassName="active" onClick={handleClose}>
                  <Box sx={{ display: 'flex', width: 16, justifyContent: 'center' }}><BiFile size={14} /></Box>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>Gestion congés</Typography>
                </SubNavItem>
              )}
            </Collapse>
          </>
        )}

        <CandidatImport
          open={false}
          onClose={() => {}}
          todos={collapsed}
          modalState={modalState}
          setModalState={setModalState}
          handleOpen={handleOpen}
        />
        <ContactImport
          open={false}
          onClose={() => {}}
          todos={collapsed}
          modalState={modalStateContact}
          setModalState={setModalStateContact}
          handleOpen={handleOpenContact}
        />
      </NavScrollArea>

      {!inDrawer && <CollapseToggle>
        <Tooltip title={collapsed ? 'Élargir' : 'Réduire'} placement="right">
          <IconButton
            onClick={() => {
              setCollapsed(!collapsed);
              dispatch(setToggleSidebar(!collapsed));
            }}
            size="small"
            sx={{
              color: '#94a3b8',
              '&:hover': { color: '#03407b', background: '#f1f5f9' },
            }}
          >
            {collapsed ? <FiChevronsRight size={18} /> : <FiChevronsLeft size={18} />}
          </IconButton>
        </Tooltip>
      </CollapseToggle>}
    </NavWrapper>
  );
};

export default VerticalNavWrapper;
